import {ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Fullscreen} from './fullscreen';
import {BannerService} from '../../../banner/banner.service';
import {forkJoin, from, of} from 'rxjs'
import {catchError, map, shareReplay} from 'rxjs/operators';

import {PlayerSelectAudioComponent} from './player-select-audio/player-select-audio.component';
import {PlayerSelectVideoComponent} from './player-select-video/player-select-video.component';
import {
  mergeWithPlayable,
  parseDashRepresentations,
  parseHlsVariants,
  VideoChoice,
} from './manifest-ladder';
import {PlayerSelectTextComponent} from './player-select-text/player-select-text.component';
import {Dialog} from '@angular/cdk/dialog';
import {PlayerSelectTypeComponent} from './player-select-type/player-select-type.component';


import {clpp} from '@castlabs/prestoplay'
import '@castlabs/prestoplay/cl.mse'
import '@castlabs/prestoplay/cl.hls'
import '@castlabs/prestoplay/cl.dash'
import '@castlabs/prestoplay/cl.htmlcue'
import '@castlabs/prestoplay/cl.vtt'
import '@castlabs/prestoplay/cl.ttml'
import { environment } from '../../../../environments/environment';


const DASH_STREAM = 'DASH stream';
const HLS_STREAM = 'HLS stream';
const AUTO_SELECT = 'Autoselect';


export const STREAM_TYPES = [HLS_STREAM, DASH_STREAM, AUTO_SELECT]


/**
 * The rendition that is on screen, from a BITRATE_CHANGED event.
 *
 * `BitrateChangeDetails.rendition` is documented as "the video rendition if one could be found" --
 * so it is genuinely optional, and dropping the update when it is missing would leave the ladder
 * marking a rendition that is no longer playing. The track manager is asked instead, and only if
 * that also comes back empty is the previous value left alone by returning null.
 */
export function renditionFromBitrateEvent(event: any, fromTrackManager: () => any): any {
  const detail = event?.detail;
  if (detail?.rendition) {
    return detail.rendition;
  }
  const current = fromTrackManager();
  if (current) {
    return current;
  }
  // Nothing resolvable, but width/height are always in the detail -- enough to mark the row.
  return detail?.width && detail?.height
    ? {id: null, codec: null, width: detail.width, height: detail.height, bandwidth: detail.bandwidth ?? null}
    : null;
}

/**
 * The video renditions an operator can actually watch.
 *
 * Trick-play tracks are dropped. A `vod-default-cmaf-abr-v2` DASH manifest carries an I-frame-only
 * set beside every real one -- flagged `EssentialProperty schemeIdUri="…/trickmode"`, one frame per
 * GOP, so 0.25 fps for a 4 s segment -- which is what put each resolution in the list twice, the
 * second time at roughly a tenth of the bitrate. Selecting one gives a slideshow, not a preview.
 *
 * `Track.trickPlay` is the player's own flag for this, so nothing has to be inferred from the frame
 * rate. Its own documentation notes ABR ignores these tracks entirely and they can only be chosen
 * by hand -- which is precisely what this dialog was letting happen by accident.
 */
export function viewableVideoRenditions(videoTracks: any[]): any[] {
  return (videoTracks ?? [])
    .filter(track => !track?.trickPlay)
    .map(track => track.renditions ?? [])
    .reduce((all, renditions) => [...all, ...renditions], []);
}

@Component({
  selector: 'app-player',
  templateUrl: './player.component.html',
  styleUrls: ['./player.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: []
})
export class PlayerComponent implements OnInit, OnDestroy {
  @Input() manifest: string;
  @Input() m3u8: string;
  @Input() drmtodayToken: string;
  @Input() accessToken: string;
  @Input() abToken: string;


  player: any;
  error: any;
  airline: string;
  playerState = {
    mouseMoved: false,
    isPlaying: false,
    isMouseInFooter: false,
    progress: 0,
    duration: 0,
    isSeeking: false,
    isSelectingTrack: false,
    isFullscreen: false,
    isLoading: true,
    isInitializing: true
  }
  fullscreenManager: Fullscreen;

  videoRenditions: any[] = [];
  audioRenditions: any[] = [];
  textRenditions: any[] = [];

  activeVideoRendition: any; // clpp.Rendition
  activeAudioRendition: any; // clpp.Rendition
  activeTextRendition: any; // clpp.Rendition
  selectedStreamType = AUTO_SELECT
  progressBarContainer: HTMLElement;

  token: any;

  streamType = ''
  streamTypeDASH = false
  streamTypeHLS = false
  /**
   * The ladder as the manifest declares it, annotated with what this browser can play.
   *
   * Kept beside `videoRenditions` rather than replacing it: selection still goes through the
   * player's own rendition objects, this only adds back the ones it refused to offer.
   */
  videoChoices: VideoChoice[] = []
  /** A ladder row from the other manifest, waiting for that manifest's tracks to arrive. */
  private pendingLadderChoice: {codec: string | null; width: number | null; height: number | null} | null = null

  mouseMoveTimeout = null;

  constructor(
    private route: ActivatedRoute,
    private banner: BannerService,
    private cdRef: ChangeDetectorRef,
    private dialog: Dialog
  ) {
    this.airline = this.route.snapshot.params['airline'];
  }

  mouseEnteredControlFooter() {
    this.playerState.isMouseInFooter = true;
  }

  mouseLeftControlFooter() {
    this.playerState.isMouseInFooter = false;
  }

  startMouseMovedTimer = () => {
    this.playerState.mouseMoved = true;
    this.cdRef.detectChanges();
    clearTimeout(this.mouseMoveTimeout);
    this.mouseMoveTimeout = setTimeout(() => {
      this.playerState.mouseMoved = false;
      this.cdRef.detectChanges()
    }, 2000);
  }

  initFullscreen = () => {
    const playerContainer = document.getElementById('player-container');
    this.fullscreenManager = new Fullscreen(
      playerContainer,
      document,
      () => {
        this.playerState.isFullscreen = this.fullscreenManager.fullscreenEnabled();
      },
    );
  }

  loadPlayer() {

    const fpObs = from(navigator.requestMediaKeySystemAccess('com.apple.fps', [{
      'initDataTypes': ['cenc'],
      'audioCapabilities': [{
        'contentType': 'audio/mp4;codecs="mp4a.40.2"'
      }],
      'videoCapabilities': [{
        'contentType': 'video/mp4;codecs="avc1.42E01E"'
      }]
    }])).pipe(map(() => true), catchError(() => of(false)))

    const prObs = from(navigator.requestMediaKeySystemAccess('com.microsoft.playready', [{
      'initDataTypes': ['cenc'],
      'audioCapabilities': [{
        'contentType': 'audio/mp4;codecs="mp4a.40.2"'
      }],
      'videoCapabilities': [{
        'contentType': 'video/mp4;codecs="avc1.42E01E"'
      }]
    }])).pipe(map(() => true), catchError(() => of(false)))

    const wvObs = from(navigator.requestMediaKeySystemAccess('com.widevine.alpha', [{
      'initDataTypes': ['cenc'],
      'audioCapabilities': [{
        'contentType': 'audio/mp4;codecs="mp4a.40.2"'
      }],
      'videoCapabilities': [{
        'contentType': 'video/mp4;codecs="avc1.42E01E"'
      }]
    }])).pipe(map(() => true), catchError(() => of(false)))

    const prRobustnessObs = from(navigator.requestMediaKeySystemAccess('com.microsoft.playready', [{
      'initDataTypes': ['cenc'],
      'audioCapabilities': [{
        'robustness': '2000',
        'contentType': 'audio/mp4;codecs="mp4a.40.2"'
      }],
      'videoCapabilities': [{
        'robustness': '2000',
        'contentType': 'video/mp4;codecs="avc1.42E01E"'
      }]
    }])).pipe(map(() => true), catchError(() => of(false)))

    const wvRobustnessObs = from(navigator.requestMediaKeySystemAccess('com.widevine.alpha', [{
      'initDataTypes': ['cenc'],
      'audioCapabilities': [{
        'robustness': 'SW_SECURE_CRYPTO',
        'contentType': 'audio/mp4;codecs="mp4a.40.2"'
      }],
      'videoCapabilities': [{
        'robustness': 'SW_SECURE_CRYPTO',
        'contentType': 'video/mp4;codecs="avc1.42E01E"'
      }]
    }])).pipe(map(() => true), catchError(() => of(false)))
    const obs = forkJoin([prObs, wvObs, prRobustnessObs, wvRobustnessObs, fpObs]).pipe(shareReplay(1))
    obs.subscribe(
      v => {
        if (!(v[2] || v[3] || v[4])) {
          this.banner.open('This browser neither supports PlayReady SL 2000, Widevine SW_SECURE_CRYPTO nor Apple FairPlay . Previews are disabled.', ['Close'])
        }
        if (this.abToken) {
          const networkEngine = this.player.getNetworkEngine();
          const that = this;
          networkEngine.addRequestModifier(function (request) {
            if (request.type === clpp.net.RequestType.MANIFEST) {
              request.headers['token'] = that.abToken;
            } else if (request.type === clpp.net.RequestType.SEGMENT) {
              request.headers['token'] = that.abToken;
            }
          });
        }
        let manifest = '';
        let manifestType = null;
        let preferredDrmSystem = null;
        if (this.selectedStreamType !== AUTO_SELECT) {
          console.log('force stream type', this.selectedStreamType)
          this.streamType = this.selectedStreamType
        } else {
          if (this.m3u8 && this.manifest) {
            if (v[4]) {
              manifest = this.m3u8
              this.streamType = HLS_STREAM
            } else {
              manifest = this.manifest
              this.streamType = DASH_STREAM
            }
          } else if (this.m3u8 && !this.manifest) {
            if (v[4]) {
              this.streamType = HLS_STREAM
            } else {
              this.banner.open('Preview is only available as HLS. Please preview with a Safari browser', ['Close'])
            }
          } else if (!this.m3u8 && this.manifest) {
            if (v[2] || v[3]) {
              this.streamType = DASH_STREAM
            } else {
              this.banner.open('Preview is only available as DASH. Please preview with a Chrome or Edge browser', ['Close'])
            }
          }
        }
        if (this.streamType === DASH_STREAM) {
          manifest = this.manifest
          manifestType = clpp.Type.DASH
          preferredDrmSystem = clpp.drm.KeySystem.WIDEVINE
          this.streamTypeDASH = true
          this.streamTypeHLS = false
          clpp.install(clpp.dash.DashComponent);
        } else if (this.streamType === HLS_STREAM) {
          manifest = this.m3u8
          manifestType = clpp.Type.HLS
          preferredDrmSystem = clpp.drm.KeySystem.FAIRPLAY
          this.streamTypeDASH = false
          this.streamTypeHLS = true
          clpp.install(clpp.hls.HlsComponent);
        }
        const playerInit = {
          source: {
            url: manifest,
            type: manifestType
          },
        }
        if (this.drmtodayToken) {
          const payload = JSON.parse(atob(this.drmtodayToken.split('.')[1]))
          const optData = JSON.parse(payload['optData'])
          playerInit['drm'] = {
            env: 'DRMtoday',
            preferredDrmSystem: preferredDrmSystem,
            emeApi: clpp.drm.eme.EmeFactory.Apis.STANDARD,
            customData: {
              userId: optData['userId'],
              sessionId: optData['sessionId'],
              merchant: optData['merchant'],
              authToken: this.drmtodayToken
            }
          }
        }
        this.player.load(playerInit);
      })
    return obs

  }

  ngOnInit() {

    this.player = new clpp.Player(
      'player',
      {
        autoplay: false,
        muted: false,
        license: environment.player_license,
        textStyle: {
          fontFamily: 'Open Sans',
          fontSize: '2em',
        },
        htmlcue: {
          stretchSmpteImage: true,
        },
        // The DASH manifest declares H.264 and AV1 mutually switchable -- the packager runs with
        // `-allow_codec_switching`, which puts every video AdaptationSet into one
        // `adaptation-set-switching:2016` group regardless of codec. prestoplay's default strategy
        // is PREVENT, which reacts to that by "selecting only the codecs that are possible to adapt
        // within the period" -- i.e. it keeps one codec and drops the rest, which is why a
        // three-codec ladder listed AV1 only.
        //
        // SMOOTH honours the declaration instead, using SourceBuffer.changeType() where the device
        // supports it, so every codec stays in the track list. This is a preview player whose job
        // is to show what was encoded; seeing all renditions matters more here than a guaranteed
        // seamless switch. On a device with unreliable changeType() prestoplay falls back on its
        // own -- it keeps a device blocklist for exactly that.
        mediaSource: {
          codecSwitchingStrategy: clpp.CodecSwitchingStrategy.SMOOTH,
        },
      },
    );
    clpp.install(clpp.htmlcue.HtmlCueComponent);
    clpp.install(clpp.vtt.VttComponent);
    clpp.install(clpp.ttml.TtmlComponent);

    this.player.on('error', err => {
      this.banner.open(err.detail.message, ['Close'])
    })

    this.progressBarContainer = document.getElementById('progress-bar-container');
    this.initFullscreen();

    this.player.on('timeupdate', () => {
      if (!this.playerState.isSeeking) {
        this.playerState.progress = this.player.getPosition();
      }
    });
    this.player.on(clpp.events.LOADEDMETADATA, () => {
      this.playerState.duration = this.player.getDuration();
      this.playerState.isLoading = false;
      this.playerState.isInitializing = false;
      this.player.getTextDisplayer().setFontColor('rgba(255, 255, 255, 1)')
      this.cdRef.detectChanges()
      this.startMouseMovedTimer(); // display controls!
    });
    this.player.on(clpp.events.SEEKING, () => {
      this.playerState.isLoading = true;
    })
    this.player.on(clpp.events.SEEKED, () => {
      this.playerState.isLoading = false;
      this.cdRef.detectChanges()
    })
    this.player.on(clpp.events.ERROR, event => {
      this.error = event.detail;
      // Logged, not only stored: this handler used to put the error in a field nothing rendered, so
      // a non-fatal one left no trace at all. Two packaging failures were diagnosed entirely from
      // elsewhere because of it.
      console.log('[preview] player error', event.detail);
    });
    // VIDEO_TRACK_CHANGED is the wrong event for "what is on screen". Its own documentation says
    // so: it fires when a rendition is *selected to be downloaded and appended to the buffer*, and
    // "is not necessarily triggered when the currently playing video track is changed". With ABR
    // holding one track for a whole session it may never fire at all, which left
    // activeVideoRendition null and the ladder stuck on "Playable" for the row it was playing.
    //
    // BITRATE_CHANGED is the one that means what we want: "triggered when the currently playing
    // video rendition changes". It also keeps the marker honest as ABR moves between renditions.
    this.player.on(clpp.events.BITRATE_CHANGED, event => {
      // No detectChanges() here: the binding that feeds the ladder lives in the *parent* template,
      // which this view's change detector does not cover. zone.js instruments the player's
      // callbacks, so Angular runs a normal cycle over the whole tree by itself.
      this.activeVideoRendition = renditionFromBitrateEvent(
        event, () => this.player.getTrackManager().getVideoRendition());
    });
    this.player.on(clpp.events.VIDEO_TRACK_CHANGED, () => {
      // Still useful as a coarse fallback: a track switch means whatever was on screen is stale.
      this.activeVideoRendition = this.player.getTrackManager().getVideoRendition();
    });
    this.player.on(clpp.events.AUDIO_TRACK_CHANGED, () => {
      this.activeAudioRendition = this.player.getTrackManager().getAudioRendition();
    });
    this.player.on(clpp.events.TEXT_TRACK_CHANGED, () => {
      this.activeTextRendition = this.player.getTrackManager().getTextRendition();
    });
    this.player.on(clpp.events.TRACKS_ADDED, () => {
      const trackManager = this.player.getTrackManager();

      this.videoRenditions = viewableVideoRenditions(trackManager.getVideoTracks())
      this._loadDeclaredLadder();
      if (this.pendingLadderChoice) {
        const pending = this.pendingLadderChoice;
        this.pendingLadderChoice = null;
        this._selectRendition(pending);
      }
      this.audioRenditions = trackManager.getAudioTracks().map(
        audioTrack => audioTrack.renditions).reduce((prev, curr) => [...prev, ...curr], [])
      // No forced setAudioRendition here. It used to pin audioRenditions[0] the moment tracks were
      // announced, which threw "Cannot read properties of null (reading 'codecs')" out of cl.mse --
      // TRACKS_ADDED fires before the source buffers exist, so there is nothing to switch yet. The
      // throw aborted the load and the player started over, which is why the track dump appeared
      // three times with fresh ids on a single page view.
      //
      // Nothing needed it: the player picks its own default audio track (DEFAULT=YES in HLS, the
      // first AdaptationSet in DASH), AUDIO_TRACK_CHANGED fills in activeAudioRendition, and the
      // dialog switches renditions on demand. Forcing index 0 also pinned an arbitrary language --
      // 'en' ahead of 'und' purely by manifest order -- and defeated ABR from the first frame.
      this.textRenditions = trackManager.getTextTracks().map(
        textTrack => textTrack.renditions).reduce((prev, curr) => [...prev, ...curr], [])
    });

    this.loadPlayer()
  }

  /**
   * Fetch and parse the manifest so the dialog can list what was encoded, not only what plays.
   *
   * Failure is deliberately quiet: this only enriches the list, so a manifest that cannot be read
   * leaves the previous behaviour intact rather than breaking the player. The same URL the player
   * just loaded is used, so it is already in the browser cache.
   */
  private _loadDeclaredLadder(): void {
    const url = this.streamType === HLS_STREAM ? this.m3u8 : this.manifest;
    const renditions = this.videoRenditions;
    if (!url) {
      this.videoChoices = mergeWithPlayable([], renditions);
      return;
    }
    const isHls = this.streamType === HLS_STREAM;
    fetch(url)
      .then(response => response.ok ? response.text() : Promise.reject(response.status))
      .then(text => {
        const declared = isHls ? parseHlsVariants(text) : parseDashRepresentations(text);
        this.videoChoices = mergeWithPlayable(declared, renditions);
        this.cdRef.detectChanges();
      })
      .catch(() => {
        this.videoChoices = mergeWithPlayable([], renditions);
      });
  }

  /**
   * Play the rendition a ladder row names.
   *
   * The row carries codec and resolution, not a rendition id: the ladder is built from the
   * manifests and has no idea what the player called anything. Resolving it here keeps the two
   * decoupled and means a row of the *other* manifest can be asked for -- that costs a reload, so
   * the choice is remembered and applied once tracks arrive.
   */
  applyLadderChoice(entry: {streamType: string; codec: string | null; width: number | null; height: number | null}): void {
    if (entry.streamType !== this.streamType) {
      this.pendingLadderChoice = entry;
      this.selectedStreamType = entry.streamType;
      this.loadPlayer();
      return;
    }
    this._selectRendition(entry);
  }

  /** Match a ladder row onto a player rendition by codec family and resolution. */
  private _selectRendition(entry: {codec: string | null; width: number | null; height: number | null}): void {
    const family = (codec: any) => String(codec ?? '').split('.')[0].toLowerCase();
    const match = this.videoRenditions.find(r =>
      family(r.codec) === family(entry.codec) && r.width === entry.width && r.height === entry.height);
    if (match) {
      this.player.getTrackManager().setVideoRendition(match, true);
    }
  }

  /**
   * Select an audio rendition by id, for the dropdown outside the player.
   *
   * Same resolution step as the video dialog, kept here rather than in the panel so nothing outside
   * the player has to know how renditions are numbered.
   */
  applyAudioChoice(id: number | null): void {
    const match = this.audioRenditions.find(r => r.id === id);
    if (match) {
      this.player.getTrackManager().setAudioRendition(match, true);
    }
  }

  /** Select a subtitle rendition, or turn subtitles off with null -- which is a real choice here. */
  applyTextChoice(id: number | null): void {
    if (id === null || id === undefined) {
      this.player.getTrackManager().setTextRendition(null, true);
      return;
    }
    const match = this.textRenditions.find(r => r.id === id);
    if (match) {
      this.player.getTrackManager().setTextRendition(match, true);
    }
  }

  ngOnDestroy(): void {
    this.player.destroy();
  }

  play() {
    if (this.playerState.isLoading) {
      return;
    }
    this.player.play().then(
      () => {
        this.playerState.isPlaying = true;
      }
    );
  }

  pause() {
    if (this.playerState.isLoading) {
      return;
    }
    this.player.pause().then(
      () => {
        this.playerState.isPlaying = false;
      }
    );
  }

  isVisible() {
    const {isSeeking, mouseMoved, isSelectingTrack, isInitializing, isMouseInFooter} = this.playerState;
    return (isSeeking || mouseMoved || isSelectingTrack || isMouseInFooter) && !isInitializing;
  }

  getProgressPercent() {
    const duration = Math.max(this.playerState.duration, 0.001);
    return (this.playerState.progress / duration * 100).toFixed(1) + '%';
  }

  getFormattedProgress() {
    const {progress, duration} = this.playerState;
    const padZero = num => num.toString().padStart(2, '0');
    const hours = Math.floor(progress / 3600);
    const minutes = Math.floor((progress - 3600 * hours) / 60);
    const seconds = Math.floor(progress - 3600 * hours - 60 * minutes);
    return `${padZero(hours)}:${padZero(minutes)}:${padZero(seconds)}`;
  }

  getFormattedMediaRunTime() {
    const {progress, duration} = this.playerState;
    const padZero = num => num.toString().padStart(2, '0');
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration - 3600 * hours) / 60);
    const seconds = Math.floor(duration - 3600 * hours - 60 * minutes);
    return `${padZero(hours)}:${padZero(minutes)}:${padZero(seconds)}`;
  }


  startSeek(event) {
    const updateProgressOnSeek = (updateProgressOnSeekEvent) => {
      const boundingRect = this.progressBarContainer.getBoundingClientRect();
      this.playerState.progress = this.playerState.duration * Math.max(
        Math.min((updateProgressOnSeekEvent.pageX - boundingRect.left) / boundingRect.width, 1), 0);
    };
    const stopSeek = () => {
      this.playerState.isSeeking = false;
      document.removeEventListener('mousemove', updateProgressOnSeek)
      document.removeEventListener('mouseup', stopSeek);
      this.player.seek(this.playerState.progress);
    };
    document.addEventListener('mouseup', stopSeek);
    document.addEventListener('mousemove', updateProgressOnSeek);
    this.playerState.isSeeking = true;
    updateProgressOnSeek(event);
  }


  selectStreamType() {
    this.playerState.isSelectingTrack = true
    this.dialog.open<string>(PlayerSelectTypeComponent, {
      data: {
        selectedStreamType: this.selectedStreamType,
        streamTypeHLS: this.streamTypeHLS,
        streamTypeDASH: this.streamTypeDASH
      }
    }).closed.subscribe(r => {
      if (r) {
        this.selectedStreamType = r;
        this.loadPlayer();
      }
      this.playerState.isSelectingTrack = false;
    });
  }

  selectAudioStream() {
    this.playerState.isSelectingTrack = true;
    this.dialog.open<number>(PlayerSelectAudioComponent, {
      data: {
        renditions: this.audioRenditions,
        activeRenditionId: this.activeAudioRendition.id,
        streamTypeHLS: this.streamTypeHLS,
        streamTypeDASH: this.streamTypeDASH
      }
    }).closed.subscribe(r => {
      this.playerState.isSelectingTrack = false;
      if (r && r >= 0) {
        this.player.getTrackManager().setAudioRendition(this.audioRenditions.filter(ar => ar.id === r)[0], true);
      }
    });
  }

  selectVideoStream() {
    this.playerState.isSelectingTrack = true;
    this.dialog.open<number>(PlayerSelectVideoComponent, {
      data: {
        renditions: this.videoChoices.length ? this.videoChoices : this.videoRenditions,
        activeRenditionId: this.activeVideoRendition ? this.activeVideoRendition.id : -1,
      }
    }).closed.subscribe(r => {
      this.playerState.isSelectingTrack = false;
      if (r && r >= 0) {
        this.player.getTrackManager().setVideoRendition(this.videoRenditions.filter(ar => ar.id === r)[0], true);
      }
    });
  }

  selectTextStream() {
    this.playerState.isSelectingTrack = true;
    this.dialog.open<number>(PlayerSelectTextComponent, {
      data: {
        renditions: this.textRenditions,
        activeRenditionId: this.activeTextRendition ? this.activeTextRendition.id : -1,
        streamTypeHLS: this.streamTypeHLS,
        streamTypeDASH: this.streamTypeDASH
      }
    }).closed.subscribe(r => {
      this.playerState.isSelectingTrack = false;
      if (r && r >= 0) {
        this.player.getTrackManager().setTextRendition(this.textRenditions.filter(ar => ar.id === r)[0], true);
      } else {
        this.player.getTrackManager().setTextRendition(null, true);
      }

    });
  }
}
